import {
  buildEndNodeId,
  type AddSuccessorStepsRequest,
  type AssigneeModel,
  type ReassigneeType
} from '@bliks-io/contracts';
import { groupRulesByOutput } from '@bliks-io/ui';
import { useAddDraftSuccessorSteps } from '@/api/mutations/useProcessDraft';
import { useDraftGesture } from '@/api/mutations/helpers/draftMutation';
import type { EdgeActionDeps } from '../edge-actions/types';
import { findStepByStableId, startEventIdsOf } from '../edge-actions/shared';
import { ensureNotTerminal, markTerminal } from '../edge-actions/successorInvariant';

function buildAssigneeBody(
  assignedTo: string,
  assignedToModel: AssigneeModel,
  assignedToType: ReassigneeType
): AddSuccessorStepsRequest {
  switch (assignedToModel) {
    case 'User':
      return { persons: [assignedTo] };
    // The orgFunctions/systems union stores the pick's own discriminator: a
    // still-pending suggestion must land as `suggestion`, or approval has
    // nothing to promote.
    case 'OrgFunction':
      return { orgFunctions: [{ type: assignedToType, id: assignedTo }] };
    case 'System':
      return { systems: [{ type: assignedToType, id: assignedTo }] };
    case 'ExternalContact':
      return { externalContacts: [assignedTo] };
  }
}

export interface ResolveUnclearWithAssigneeArgs {
  predecessorStepId: string;
  branchKey?: string;
  assignedTo: string;
  assignedToModel: AssigneeModel;
  assignedToType: ReassigneeType;
}

export interface UnclearAssignmentActions {
  markAssignmentUnclear: (stableStepId: string) => Promise<void>;
  resolveUnclearWithAssignee: (args: ResolveUnclearWithAssigneeArgs) => Promise<void>;
}

/**
 * Hook bundling the two undoable actions that keep UNCLEAR end-nodes and
 * draft assignments in sync:
 *  - `markAssignmentUnclear` flips a draft assignment's predecessor into
 *    UNCLEAR (cascading-delete of the assignment itself is part of the
 *    reversible action).
 *  - `resolveUnclearWithAssignee` clears the predecessor's UNCLEAR status
 *    and materializes a fresh draft assignment for the picked assignee.
 */
export function useUnclearAssignmentActions(
  processId: string,
  edgeActionDeps: EdgeActionDeps
): UnclearAssignmentActions {
  const addSuccessorStepsMutation = useAddDraftSuccessorSteps(processId);
  const addSuccessorStepsAsync = addSuccessorStepsMutation.mutateAsync;

  const markAssignmentUnclear = useDraftGesture(processId, async (stableStepId: string) => {
    const assignment = edgeActionDeps
      .getCachedDraftAssignments()
      .find((a) => String(a.stableStepId) === stableStepId);
    if (!assignment) return;
    // The predecessor must be a real STEP: skip start anchors — the
    // post-M2 node-id tag form and the legacy 'START' sentinel alike (an
    // UNCLEAR flip targets the upstream step's successors, which a start
    // event doesn't have).
    const startEventIds = startEventIdsOf(edgeActionDeps);
    const sourceStep = (assignment.inputs ?? [])
      .flatMap((input) => (input.inputType === 'previous_output' ? (input.sourceSteps ?? []) : []))
      .find((src) => String(src.stepId) !== 'START' && !startEventIds.has(String(src.stepId)));
    if (!sourceStep) return;
    const predecessorStableId = String(sourceStep.stepId);
    const predecessor = findStepByStableId(edgeActionDeps.getCachedSteps(), predecessorStableId);
    if (!predecessor) return;
    // The canvas keys a decision output's handle and end node by the first
    // rule of its output group; a task step has only its task handle.
    const boundRuleId = sourceStep.ruleIds?.[0];
    const ruleIdStr =
      predecessor.actionType === 'decision' && boundRuleId
        ? groupRulesByOutput(predecessor.rules ?? []).ruleIdToRepresentative.get(
            String(boundRuleId)
          )
        : undefined;

    // Without a saved position the amber end-node falls back to the layout
    // sentinel and jumps visibly when the flip lands. Seed from the
    // assignment's current coords (Y is re-derived by the swimlane layout).
    const assignmentPos = edgeActionDeps.getPositionsById().get(stableStepId);
    const endPositions = assignmentPos && [
      {
        nodeId: buildEndNodeId(predecessorStableId, ruleIdStr),
        x: assignmentPos.x,
        y: assignmentPos.y
      }
    ];

    await markTerminal(
      predecessorStableId,
      ruleIdStr ? [ruleIdStr] : undefined,
      { status: 'UNCLEAR', endPositions },
      edgeActionDeps
    );
  });

  const resolveUnclearWithAssignee = useDraftGesture(
    processId,
    async ({
      predecessorStepId,
      branchKey,
      assignedTo,
      assignedToModel,
      assignedToType
    }: ResolveUnclearWithAssigneeArgs) => {
      const predecessor = findStepByStableId(edgeActionDeps.getCachedSteps(), predecessorStepId);
      if (!predecessor) return;

      const ruleId =
        branchKey && (predecessor.rules ?? []).some((r) => r._id === branchKey)
          ? branchKey
          : undefined;

      const body = buildAssigneeBody(assignedTo, assignedToModel, assignedToType);

      // Seed the new assignment at the end-node's saved position so the node
      // doesn't visibly jump on resolve. The backend materializes `position`
      // into `stepPositions`; the swimlane layout re-derives Y at render time.
      const endNodeId = buildEndNodeId(predecessorStepId, ruleId);
      const oldEndPos = edgeActionDeps
        .getCachedGeneratedPositions()
        .find((g) => g.stepId === endNodeId);

      // Optimistic-only: the add-successor endpoint clears the predecessor's
      // UNCLEAR marker in the same request.
      ensureNotTerminal(predecessorStepId, ruleId, edgeActionDeps);

      const baseBody = ruleId ? { ...body, ruleId } : body;
      const finalBody = oldEndPos
        ? { ...baseBody, position: { x: oldEndPos.x, y: oldEndPos.y } }
        : baseBody;
      await addSuccessorStepsAsync({ stepId: predecessor._id, body: finalBody });
    }
  );

  return { markAssignmentUnclear, resolveUnclearWithAssignee };
}
